import { Link, useParams } from "react-router-dom";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { HOSTELS, getHostel, totalBoarders, totalRooms } from "../lib/hostels";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function HostelDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const h = getHostel(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!h) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Hostel</div>
          <h1 className="ch-name">Hostel not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.hostels} style={{ color: "var(--gold-400)" }}>← Back to hostels</Link>
          </p>
        </div>
      </section>
    );
  }

  const others = HOSTELS.filter((x) => x.slug !== h.slug);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`HOSTEL · ${h.short}`}
        title={h.name}
        back={{ label: "All hostels", to: ROUTES.hostels }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.hostels}>Hostels</Link>
            <span aria-hidden>/</span>
            <span>{h.gender}</span>
          </nav>
          <div className="ch-eyebrow">{h.gender} · Started {h.estd}</div>
          <span className="ch-short">{h.short}</span>
          <h1 className="ch-name">{h.name}</h1>
          <p className="ch-summary">{h.intro[0]}</p>

          <div className="ch-stats">
            <div>
              <div className="num">{totalBoarders(h)}</div>
              <div className="lbl">Boarders</div>
            </div>
            <div>
              <div className="num">{totalRooms(h)}</div>
              <div className="lbl">Rooms</div>
            </div>
            <div>
              <div className="num">{h.blocks.length}</div>
              <div className="lbl">Blocks itemised</div>
            </div>
            <div>
              <div className="num">{h.estd}</div>
              <div className="lbl">Started</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>About</h2>
              {h.intro.slice(1).map((p, i) => <p key={i}>{p}</p>)}
              {h.intro.length === 1 && <p>{h.intro[0]}</p>}

              {h.photos?.[0] && (
                <div className="news-article-image">
                  <Figure
                    media={h.photos[0]}
                    fallbackLabel={`${h.short} · HOSTEL`}
                    aspect="2/1"
                    sizes="(max-width: 900px) 100vw, 700px"
                  />
                </div>
              )}

              <h2>Blocks</h2>
              <table className="center-projects">
                <thead>
                  <tr><th>Block</th><th>Rooms</th><th>Boarders</th></tr>
                </thead>
                <tbody>
                  {h.blocks.map((b, i) => (
                    <tr key={i}>
                      <td className="tt">{b.name}</td>
                      <td className="amt">{b.rooms}</td>
                      <td className="amt">{b.boarders}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="tt"><b>Total</b></td>
                    <td className="amt"><b>{totalRooms(h)}</b></td>
                    <td className="amt"><b>{totalBoarders(h)}</b></td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                As itemised by the institute. Its own summary for this hostel lists{" "}
                {h.facts.find((f) => f.label === "Blocks")?.value} blocks and{" "}
                {h.facts.find((f) => f.label === "Rooms")?.value} rooms.
              </p>

              <h2>Fees · published for {h.fee.year}</h2>
              <table className="center-projects">
                <thead>
                  <tr><th>Particular</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {h.fee.rows.map((r, i) => (
                    <tr key={i}>
                      <td className="tt">{r.particular}</td>
                      <td className="amt">{r.amount}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="tt"><b>Total</b></td>
                    <td className="amt"><b>{h.fee.total}</b></td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Deposit is refundable. Confirm the current year's fee and the payment details with
                the hostel office before paying.
              </p>

              <h2>Facilities &amp; amenities</h2>
              <ul className="detail-list">
                {h.amenities.map((a, i) => <li key={i}>{a}</li>)}
              </ul>

              <h2>Admission procedure</h2>
              <ol className="detail-list">
                {h.admission.map((a, i) => <li key={i}>{a}</li>)}
              </ol>

              <h2>Room details</h2>
              <ul className="detail-list">
                {h.roomRules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>

              {h.note && (
                <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  <b>Note</b> · {h.note}
                </p>
              )}

              {h.photos && h.photos.length > 1 && (
                <>
                  <h2>Gallery</h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "var(--space-4)",
                    }}
                  >
                    {h.photos.slice(1).map((p, i) => (
                      <figure key={i} style={{ margin: 0 }}>
                        <Figure media={p} fallbackLabel="HOSTEL" aspect="2/1" sizes="(max-width: 760px) 100vw, 340px" />
                        <figcaption
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "var(--text-muted)",
                            marginTop: 8,
                          }}
                        >
                          {p.alt}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Wing</div>
                <div className="dmc-value">{h.gender}</div>
              </div>
              {h.facts.map((f, i) => (
                <div key={i} className="dmc-block">
                  <div className="dmc-label">{f.label}</div>
                  <div className="dmc-value">{f.value}</div>
                </div>
              ))}
              <div className="dmc-block">
                <div className="dmc-label">Contacts</div>
                <div className="dmc-value">
                  {h.contacts.map((c, i) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{c.role}</span><br />
                      {c.name}
                      {c.phone && <> · <a href={`tel:+91${c.phone}`}>{c.phone}</a></>}
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">Other residences</div>
              <h2>The other hostel on campus</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.hostelDetail}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{totalBoarders(o)}</span>
                    <span className="er-month">BOARDERS</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.gender}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">Since {o.estd}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
